import React, { useRef, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { FormattedMessage, FormattedHTMLMessage } from "react-intl";
import { sortBy } from "lodash";

import CourseCard from "../../components/CourseCard";
import ProviderCard from "../../components/ProviderCard";
import { Heading3, Hidden } from "../../components";
import Lead from "./Lead";
import Franchise from "./Franchise";

import { isSameLocation, withinViewport } from "./SearchMap";

const Panel = styled.div`
  padding: 0 0.5rem 1rem;

  @media (${props => props.theme.desktopScreen}) {
    padding: 0 0.5rem 2rem;
  }
`;

const Section = styled.section`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled(Heading3)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.75rem 0.5rem;
  font-size: 1rem;
  color: ${props => props.theme.dark};
`;

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.4rem;
  border-radius: 0.75rem;
  background-color: ${props => props.theme.primary};
  color: ${props => props.theme.white};
  font-size: 0.75rem;
`;

const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
`;

/* Lifts the card of the marker the visitor picked, so list and map stay tied together. */
const Item = styled.li`
  position: relative;
  min-width: 100%;
  margin-bottom: 0.75rem;
  border-radius: ${props => props.theme.borderRadiusMedium};
  transition: transform 0.25s, box-shadow 0.25s;
  scroll-margin-top: 1rem;

  ${({ focus, theme }) =>
    focus &&
    `
    transform: translateY(-0.25rem);
    box-shadow: ${theme.boxShadowLifted};
  `}

  @media (${props => props.theme.tabletScreen}) {
    min-width: 50%;
    padding: 0 0.4rem;
  }

  @media (${props => props.theme.desktopScreen}) {
    min-width: 50%;
  }
`;

const MoreResults = styled.button`
  display: block;
  width: 100%;
  margin: 0.5rem 0 1rem;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: ${props => props.theme.borderRadiusMedium};
  background-color: ${props => props.theme.white};
  box-shadow: ${props => props.theme.boxShadowSmall};
  color: ${props => props.theme.info};
  cursor: pointer;

  strong {
    margin-right: 0.35rem;
  }

  &:hover {
    box-shadow: ${props => props.theme.boxShadow};
  }
`;

const cardFor = (result, onCategoryClick, term) => {
  switch ((result.modelName || "").toLowerCase()) {
    case "provider":
      return (
        <ProviderCard
          provider={result}
          listView={false}
          onCategoryClick={onCategoryClick}
          term={term}
        />
      );
    case "course":
      return (
        <CourseCard
          course={result}
          listView={false}
          onCategoryClick={onCategoryClick}
          provider={result.provider}
        />
      );
    default:
      return null;
  }
};

const ResultsPanel = ({
  active,
  locations,
  onlineResults,
  currentCenter,
  currentLocation,
  onSelect,
  onCategoryClick,
  onZoomOut,
  franchise,
  term,
}) => {
  const [visible, setVisible] = useState([]);
  const listRef = useRef(null);

  // Every result of every location, nearest first, with external providers pushed to the end.
  const orderResults = useCallback(() => {
    const covered = withinViewport(locations, currentCenter);

    const results = covered.reduce(
      (collected, location) =>
        collected.concat((location.results || []).map(result => ({ ...result, location }))),
      [],
    );

    setVisible(sortBy(results, result => (result.distance || 0) + (result.external ? 999999 : 0)));
  }, [locations, currentCenter]);

  useEffect(() => {
    orderResults();
  }, [orderResults]);

  // Scroll the picked marker's first card into view
  useEffect(() => {
    if (!active || !currentLocation || visible.length < 1 || !listRef.current) {
      return;
    }

    const element = listRef.current.querySelector(
      `[data-location="${currentLocation.lat},${currentLocation.lng}"]`,
    );

    if (element) {
      element.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [active, currentLocation, visible]);

  const hidden = (locations || []).length - new Set(visible.map(r => r.location.key)).size;

  if (visible.length < 1 && onlineResults.length < 1) {
    return franchise ? (
      <Panel>
        <Franchise franchise={franchise} />
      </Panel>
    ) : null;
  }

  return (
    <Panel>
      {franchise && <Franchise franchise={franchise} />}

      {visible.length > 0 && (
        <Section>
          <SectionTitle>
            <FormattedMessage id="pages.SearchMap.onMap" />
            <Count>{visible.length}</Count>
          </SectionTitle>
          <List ref={listRef}>
            {visible.map(result => (
              <Item
                key={`${result.modelName}-${result.id}-${result.location.key}`}
                data-location={`${result.location.lat},${result.location.lng}`}
                focus={currentLocation && isSameLocation(currentLocation, result.location)}
                onClick={() => onSelect(result.location)}
              >
                {cardFor(result, onCategoryClick, term)}
              </Item>
            ))}
          </List>
        </Section>
      )}

      {hidden > 0 && (
        <MoreResults onClick={onZoomOut}>
          <strong>{hidden}</strong>
          <FormattedHTMLMessage id="pages.SearchMap.moreResultsNear" />
        </MoreResults>
      )}

      {onlineResults.length > 0 && (
        <Section>
          <SectionTitle>
            <FormattedMessage id="pages.SearchMap.onlineSection" />
            <Count>{onlineResults.length}</Count>
          </SectionTitle>
          <List>
            {onlineResults.map(result => (
              <Item key={`online-${result.modelName}-${result.id}`}>
                {cardFor(result, onCategoryClick, term)}
              </Item>
            ))}
          </List>
        </Section>
      )}

      <Hidden size={["sm", "md"]}>
        <Lead />
      </Hidden>
    </Panel>
  );
};

ResultsPanel.propTypes = {
  active: PropTypes.bool,
  locations: PropTypes.array.isRequired,
  onlineResults: PropTypes.array,
  currentCenter: PropTypes.object,
  currentLocation: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  onCategoryClick: PropTypes.func.isRequired,
  onZoomOut: PropTypes.func.isRequired,
  franchise: PropTypes.object,
  term: PropTypes.string,
};

ResultsPanel.defaultProps = {
  active: false,
  onlineResults: [],
  currentCenter: null,
  currentLocation: null,
  franchise: null,
  term: "",
};

export default ResultsPanel;
